import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Stethoscope, GraduationCap, Building2, Utensils, Truck, Hotel,
  School, BookOpen, HeartPulse, Pill, Warehouse, ArrowRight,
} from "lucide-react";
import { ServicePage } from "@/components/sections/service-page";
import { PageShell } from "@/components/site/page-shell";

export const Route = createFileRoute("/software/industry-solutions/")({
  component: IndustryIndex,
  head: () => ({
    meta: [
      { title: "Industry Solutions — CoderLity" },
      { name: "description", content: "Pre-built software for healthcare, education, real estate, restaurants, logistics and hospitality." },
      { property: "og:title", content: "Industry Solutions — CoderLity" },
      { property: "og:description", content: "Vertical-ready software solutions that ship in weeks instead of months." },
      { property: "og:url", content: "/software/industry-solutions" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/software/industry-solutions" }],
  }),
});

type Item = { slug: string; title: string; desc: string; icon: typeof School };
const education: Item[] = [
  { slug: "school-management", title: "School Management", desc: "Explore school management", icon: School },
  { slug: "lms-platform", title: "LMS Platform", desc: "Explore lms platform", icon: BookOpen },
];
const healthcare: Item[] = [
  { slug: "hospital-management", title: "Hospital Management", desc: "Explore hospital management", icon: HeartPulse },
  { slug: "clinic-pharmacy", title: "Clinic & Pharmacy", desc: "Explore clinic & pharmacy", icon: Pill },
];
const logistics: Item[] = [
  { slug: "fleet-management", title: "Fleet Management", desc: "Explore fleet management", icon: Truck },
  { slug: "warehouse-management", title: "Warehouse Management", desc: "Explore warehouse management", icon: Warehouse },
];

function Group({ title, items }: { title: string; items: Item[] }) {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight">{title}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.slug}
                to="/software/industry-solutions/$child"
                params={{ child: it.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground inline-flex items-center gap-1">
                  {it.desc} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function IndustryIndex() {
  return (
    <>
      <ServicePage data={data} />
      <PageShell>
        <Group title="Education" items={education} />
        <Group title="Healthcare" items={healthcare} />
        <Group title="Logistics" items={logistics} />
      </PageShell>
    </>
  );
}

const data = {
  eyebrow: "Software",
  title: "Industry Solutions",
  sub: "Pre-built systems for vertical industries.",
  intro:
    "Why build from scratch when 80% of your industry's needs are already solved? Our vertical solutions ship pre-configured for healthcare, education, real estate, restaurants, logistics and hospitality — compliance built in, workflows tuned to industry standards, and integrations to the tools your sector already uses. Customize the last 20% to make it yours.",
  features: [
    { icon: Stethoscope, title: "Healthcare", description: "Clinics, telehealth, EMR/EHR, appointments and HIPAA-ready hosting." },
    { icon: GraduationCap, title: "Education", description: "LMS, student information systems, fee management and parent portals." },
    { icon: Building2, title: "Real estate", description: "Listings, CRM, lease management and tenant portals." },
    { icon: Utensils, title: "Restaurants", description: "POS, online ordering, kitchen display, delivery dispatch and loyalty." },
    { icon: Truck, title: "Logistics", description: "Fleet management, route optimization, proof of delivery and tracking." },
    { icon: Hotel, title: "Hospitality", description: "PMS, channel manager, housekeeping, guest experience and revenue management." },
  ],
  process: [
    { step: "01", title: "Industry fit", description: "Pick the vertical solution that matches your business model." },
    { step: "02", title: "Configure", description: "Branding, workflows, integrations and user roles." },
    { step: "03", title: "Customize", description: "Layer your unique processes on top of the base platform." },
    { step: "04", title: "Launch", description: "Training, data migration, parallel run and go-live." },
  ],
  deliverables: [
    "Industry-specific platform configured for your business",
    "Branded portals for staff, customers and partners",
    "Standard integrations for your vertical",
    "Custom workflows and reports",
    "Data migration from your existing systems",
    "Training, documentation and post-launch support",
  ],
  techStack: ["React + Node", "PostgreSQL", "Supabase", "HIPAA-ready hosting", "Twilio", "Stripe", "Vertical-specific APIs"],
  pricing: [
    { name: "Standard config", price: "$10k", features: ["Vertical solution as-is", "Branding + setup", "Up to 25 users", "4 weeks"] },
    { name: "Configured + custom", price: "$30k", period: "project", highlighted: true, features: ["Standard + custom workflows", "Custom integrations", "Data migration", "8–12 weeks"] },
    { name: "Managed solution", price: "From $1.5k", period: "month", features: ["Hosting + backups", "Ongoing customization", "User support", "Compliance monitoring"] },
  ],
  faqs: [
    { q: "Which industries do you cover today?", a: "Healthcare, education, real estate, restaurants, logistics and hospitality. We add new verticals quarterly — ask about your industry." },
    { q: "How customizable is it?", a: "Highly. The base platform covers 80% of needs; the other 20% we build to spec. You can also extend yourself via our APIs." },
    { q: "Is it compliant for regulated industries?", a: "Yes — HIPAA for healthcare, PCI for payments, and GDPR/SOC 2 across all verticals. Compliance docs available on request." },
    { q: "Can you integrate with our existing systems?", a: "Yes. Every vertical solution ships with REST APIs, webhooks and pre-built integrations with the leading tools in that industry." },
  ],
};
